import { useMemo, useState } from 'react'
import { Alert, Center, Loader, Tabs } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { useSearchParams } from 'react-router-dom'
import { ACCENT } from '@/components/ui/primaryButton.tokens'
import { DirtyDot } from '@/components/ui/SaveFooter'
import { useMyProfile } from '@/hooks/useMyProfile'
import { useWarnBeforeUnload } from '@/hooks/useWarnBeforeUnload'
import { PersonalInformationTab } from './PersonalInformationTab'
import { ProfessionalDetailsTab } from './ProfessionalDetailsTab'
import { ProfileSummaryCard } from './ProfileSummaryCard'
import { SecurityTab } from './SecurityTab'

// The active tab lives in the URL so a reload lands on the same tab, as on Provider Operations.
const TAB_QUERY_PARAM = 'tab'
const PROFILE_TABS = ['personal', 'professional', 'security'] as const
type ProfileTab = (typeof PROFILE_TABS)[number]
const DEFAULT_TAB: ProfileTab = 'personal'

function parseTab(value: string | null, isClinical: boolean): ProfileTab {
  if (value === 'professional' && !isClinical) return DEFAULT_TAB
  return (PROFILE_TABS as readonly string[]).includes(value ?? '')
    ? (value as ProfileTab)
    : DEFAULT_TAB
}

/**
 * My Profile (see the glossary): the summary card beside the tabs. Personal information and
 * Security are for everyone; Professional details only for a member with an active Medical
 * doctor role. Each tab is its own component carrying its own form and Save, and reports
 * whether it holds unsaved edits: a dirty tab carries a dot after its label, and the browser
 * asks before a reload or tab close while any tab is dirty. Panels stay mounted so switching
 * tabs keeps every tab's edits.
 */
export function MyProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: profile, isLoading, isError } = useMyProfile()
  const [dirtyTabs, setDirtyTabs] = useState<Partial<Record<ProfileTab, boolean>>>({})
  // One stable setter per tab, so the tabs' dirty-reporting effects do not re-run per render.
  const setTabDirty = useMemo(
    () =>
      Object.fromEntries(
        PROFILE_TABS.map((tab) => [
          tab,
          (dirty: boolean) =>
            setDirtyTabs((previous) =>
              previous[tab] === dirty ? previous : { ...previous, [tab]: dirty },
            ),
        ]),
      ) as Record<ProfileTab, (dirty: boolean) => void>,
    [],
  )
  useWarnBeforeUnload(Object.values(dirtyTabs).some(Boolean))

  const selectTab = (value: string | null) => {
    if (!value) return
    setSearchParams(
      (previous) => {
        previous.set(TAB_QUERY_PARAM, value)
        return previous
      },
      { replace: true },
    )
  }

  return (
    <div className="box-border min-h-[calc(100vh-64px)] bg-[#F6F7F9] p-4 text-[#1F2733]">
      <div className="mx-auto flex max-w-[1040px] flex-col gap-4 md:flex-row md:items-start">
        {isLoading && (
          <Center className="w-full py-16">
            <Loader />
          </Center>
        )}

        {isError && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Could not load your profile"
            className="w-full"
          >
            Try again in a moment. If this keeps happening, contact an administrator.
          </Alert>
        )}

        {profile && (
          <>
            <ProfileSummaryCard profile={profile} />
            <div className="min-w-0 flex-1 rounded-2xl border border-[#E7E9EC] bg-white p-6 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]">
              <Tabs
                value={parseTab(searchParams.get(TAB_QUERY_PARAM), profile.isClinical)}
                onChange={selectTab}
                color={ACCENT}
              >
                <Tabs.List>
                  <Tabs.Tab
                    value="personal"
                    rightSection={dirtyTabs.personal ? <DirtyDot /> : undefined}
                  >
                    Personal information
                  </Tabs.Tab>
                  {profile.isClinical && (
                    <Tabs.Tab
                      value="professional"
                      rightSection={dirtyTabs.professional ? <DirtyDot /> : undefined}
                    >
                      Professional details
                    </Tabs.Tab>
                  )}
                  <Tabs.Tab
                    value="security"
                    rightSection={dirtyTabs.security ? <DirtyDot /> : undefined}
                  >
                    Security
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="personal" pt="md">
                  <PersonalInformationTab profile={profile} onDirtyChange={setTabDirty.personal} />
                </Tabs.Panel>
                {profile.isClinical && (
                  <Tabs.Panel value="professional" pt="md">
                    <ProfessionalDetailsTab
                      profile={profile}
                      onDirtyChange={setTabDirty.professional}
                    />
                  </Tabs.Panel>
                )}
                <Tabs.Panel value="security" pt="md">
                  <SecurityTab onDirtyChange={setTabDirty.security} />
                </Tabs.Panel>
              </Tabs>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
